import { useMemo } from 'react';
import MessageContainer from './MessageContainer/MessageContainer';
import ChatInput from './ChatInput/ChatInput';
import Home from '../Home/Home';
import DemoInfoBox from '../Demo/DemoInfoBox';
import { SplitHorizontal } from '@phosphor-icons/react';
import { Button } from '../Utility/Button';
import useChat from '../../Hooks/useChat';

type ChatProps = {
    chatId?: string;
    promptPreview?: boolean;
    assistantIdentifier?: string;
    initMessage?: string;
};

function Chat({
    chatId: propChatId,
    promptPreview: propPromptPreview,
    assistantIdentifier: propAssistantIdentifier,
    initMessage: propInitMessage,
}: ChatProps) {
    const {
        chat,
        i18n,
        dlpState,
        sliceMessages,
        socket,
        messagesEndRef,
        loadingAnimation,
        promptPreview,
        chatContainerStyles,
        websocketError,
        chatId,
        keyboardVisible,
        dragZoneRootProps,
        dragZoneInputProps,
        demo,
        showSplitButton,
        resizerRef,
        isDragActive,
        chatLoaded,
        handleMouseDown,
        resizeContainerRef,
        chatMessages,
        initMessage,
        showInput,
    } = useChat({
        chatId: propChatId,
        promptPreview: propPromptPreview,
        assistantIdentifier: propAssistantIdentifier,
        initMessage: propInitMessage,
    });

    const messageContainer = useMemo(
        () =>
            chatLoaded ? (
                <MessageContainer
                    messages={chatMessages}
                    assistantName={chat.prompt_name}
                    assistantImage={chat.prompt_image}
                    dlpActive={chat.dlp_active}
                    dlpState={dlpState}
                    sliceMessages={sliceMessages}
                    socket={socket}
                    messagesEndRef={messagesEndRef}
                    loadingAnimation={loadingAnimation}
                    isPreviewPrompt={promptPreview}
                    chatId={chatId}
                />
            ) : null,
        [
            chatMessages,
            chat.prompt_name,
            chat.prompt_image,
            chat.dlp_active,
            dlpState,
            sliceMessages,
            socket,
            messagesEndRef,
            loadingAnimation,
            promptPreview,
            chatLoaded,
            chatId,
        ],
    );

    const webSocketErrorMessage = useMemo(
        () => (
            <div className="mb-5 rounded-xl bg-red-200 p-5" style={chatContainerStyles}>
                {i18n.translate({ id: 'websocketError', sourceName: 'Chat' })}
            </div>
        ),
        [i18n, chatContainerStyles],
    );

    const chatInput = useMemo(() => {
        if (showInput || promptPreview) {
            return chatLoaded ? <ChatInput chat={chat} initMessage={initMessage} /> : null;
        } else {
            return (
                <div className="mb-5 rounded-xl bg-chatInput-background p-5" style={chatContainerStyles}>
                    {i18n.translate({ id: 'readOnly', sourceName: 'Chat' })}
                </div>
            );
        }
    }, [chatLoaded, chat, initMessage, showInput, chatContainerStyles, i18n, promptPreview]);

    return (
        <div
            {...dragZoneRootProps()}
            className={`chat-container flex relative flex-col ${
                promptPreview ? 'h-full' : 'h-[var(--app-height)] xl:pt-0'
            } ${keyboardVisible ? 'pb-0' : 'pb-[env(safe-area-inset-bottom)]'}`}
        >
            <input className="hidden" {...dragZoneInputProps()} />
            {isDragActive && (
                <div className="absolute z-[999] top-0 left-0 right-0 bottom-0 flex items-center justify-center bg-gray text-white bg-opacity-75 text-2xl">
                    {i18n.translate({ id: 'chatDragFiles.conversation', sourceName: 'Chat' })}
                </div>
            )}

            {chatId === undefined ? (
                <>
                    <Home initMessage={initMessage} />
                </>
            ) : (
                <>
                    {promptPreview && <h3>{i18n.translate({ id: 'testAssistant', sourceName: 'Prompt' })}</h3>}
                    {messageContainer}

                    {!promptPreview && demo && (
                        <div className="px-8 hidden md:block container" style={chatContainerStyles}>
                            <div className="w-full">
                                <DemoInfoBox />
                            </div>
                        </div>
                    )}
                    <div
                        className={`input-wrapper relative w-full pb-0  px-0 flex m-auto ${
                            promptPreview ? 'pb-0' : 'sm:pb-3'
                        }`}
                    >
                        <div
                            className={`relative min-h-[60px] h-fit container ${promptPreview ? '' : ' sm:px-3'}`}
                            style={
                                window.location.pathname !== '/start' && !promptPreview
                                    ? chatContainerStyles
                                    : undefined
                            }
                        >
                            {websocketError ? webSocketErrorMessage : chatInput}
                        </div>
                    </div>

                    {!promptPreview && showSplitButton && (
                        <div className="relative container" style={chatContainerStyles}>
                            <div className="absolute hidden sm:block right-0 h-[300px] bottom-[calc(var(--app-height)/2-150px)] border-r-2 border-navHeader-border">
                                <Button
                                    variant="secondaryBigRound"
                                    className="absolute right-[-26px] bottom-[126px]"
                                    elementRef={resizerRef}
                                    onMouseDown={handleMouseDown}
                                >
                                    <SplitHorizontal size={24} />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}
            <div ref={resizeContainerRef} style={chatContainerStyles} className="container"></div>
            <div className="clean-container container"></div>
        </div>
    );
}

export default Chat;
